import { useMemo, useCallback, useState } from 'react'
import { IUserViewModel } from '../../types/interfaces'
import { GridRow, ServiceKindHashMap } from './DeltaContracts'
import { ServiceKind } from '@ail/contracts-api'
import { formatCurrencyToSwissLocale, getDeltaIconPerKind, roundUpToFixed } from '../../helpers/Utils'
import ProgressCircle2 from '../common/ProgressCircle2/ProgressCircle2'
import { useTranslation } from 'react-i18next'

interface TotalPerKind {
  actual: ServiceKindHashMap
  expected: ServiceKindHashMap
  totalExpectedOverall: number
  totalActualOverall: number
  expectedKM: number
  actualKM: number
}
interface Props {
  rows: GridRow[]
  kinds: string[]
  user?: IUserViewModel | null
  updateGraphVisibility: (visibility: boolean) => void
  isInternfleetPro: boolean
}

export default function GraphsJSX({ rows, kinds, user, updateGraphVisibility, isInternfleetPro }: Props) {
  const [enableExtraGraphs, setEnableExtraGraphs] = useState(user?.isInternalUser || isInternfleetPro ? true : false)
  const { t } = useTranslation('common')
  const getTotalsPerKind = useCallback((rows: GridRow[]) => {
    const kindTotals: TotalPerKind = {
      actual: {},
      expected: {},
      totalExpectedOverall: 0,
      totalActualOverall: 0,
      expectedKM: 0,
      actualKM: 0,
    }

    rows.forEach((row) => {
      for (const serviceKind in row.actual) {
        const actualValue = row.actual[serviceKind] ?? 0
        const expectedValue = row.expected[serviceKind] ?? 0

        kindTotals.actual[serviceKind] = (kindTotals.actual[serviceKind] ?? 0) + actualValue
        kindTotals.expected[serviceKind] = (kindTotals.expected[serviceKind] ?? 0) + expectedValue

        kindTotals.totalActualOverall += actualValue
        kindTotals.totalExpectedOverall += expectedValue
      }

      for (const dist of row.contract.monthlyDistances ?? []) {
        kindTotals.actualKM += dist.actualMonthDistance ?? 0
        kindTotals.expectedKM += dist.expectedMonthDistance ?? 0
      }
    })
    return kindTotals
  }, [])

  const drawChart = useCallback(
    (data: TotalPerKind, kind: string) => {
      const label = t(kind.toUpperCase())
      let icon: string | undefined = getDeltaIconPerKind(kind)
      let actKm = data.actual[kind] ?? 0
      let expKm = data.expected[kind] ?? 0

      if (kind === 'KM') {
        actKm = data.actualKM ?? 0
        expKm = data.expectedKM ?? 0
        icon = undefined
      }

      if (kind === 'TOT') {
        actKm = data.totalActualOverall
        expKm = data.totalExpectedOverall
        icon = undefined
      }

      if (kind === ServiceKind.Reestablishment) {
        actKm = data.actual[kind] || data.actual[ServiceKind.General] || 0
        expKm = data.expected[kind] || data.expected[ServiceKind.General] || 0
      }

      const percentage = expKm !== 0 ? ((actKm - expKm) / expKm) * 100 : undefined
      return (
        <ProgressCircle2
          progress={actKm > expKm ? (expKm === 0 ? 1 : expKm) : actKm}
          color={actKm > expKm ? 'red' : 'green'}
          maxValue={expKm === 0 ? 1 : expKm}
          innerDetails={label}
          details1={formatCurrencyToSwissLocale(actKm, 0, 0)}
          details2={formatCurrencyToSwissLocale(expKm, 0, 0)}
          percentage={
            percentage
              ? actKm !== expKm
                ? actKm < expKm
                  ? `${roundUpToFixed(percentage)}%`
                  : `+${roundUpToFixed(percentage)}%`
                : undefined
              : undefined
          }
          icon={icon}
        />
      )
    },
    [t]
  )
  const graphsJSX = useMemo(() => {
    const data = getTotalsPerKind(rows)

    function generateCharts(range: number[]) {
      return range.map((index) => <div className="deltaTopChart">{drawChart(data, kinds[index])}</div>)
    }
    return (
      <div className="deltaTopArea" style={{ position: 'relative' }}>
        {user?.isInternalUser && (
          <div className="checkPro">
            <label>I4UPro</label>
            <input
              type="checkbox"
              checked={enableExtraGraphs}
              onChange={() => {
                setEnableExtraGraphs(!enableExtraGraphs)
                updateGraphVisibility(!enableExtraGraphs)
              }}
            />
          </div>
        )}
        <div className="col-10">
          <div className="deltaItemsList">
            <div className={`${enableExtraGraphs ? 'deltaItems' : 'deltaInLine deltaItems'}`}>
              {generateCharts([0, 1, 2, 3])}
              {enableExtraGraphs && <>{generateCharts([8])}</>}
            </div>
            <div className={`${enableExtraGraphs ? 'deltaItems' : 'deltaInLine deltaItems'}`}>
              {generateCharts([4, 5, 6, 7])}
              {enableExtraGraphs && <>{generateCharts([9])}</>}
            </div>
          </div>
        </div>
        <div className="col-2">{generateCharts([10])}</div>
      </div>
    )
  }, [getTotalsPerKind, rows, user?.isInternalUser, enableExtraGraphs, drawChart, kinds, updateGraphVisibility])

  return graphsJSX
}
